import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  DEFAULT_LANGUAGE,
  LANGUAGE_STORAGE_KEY,
  isLanguage,
  type Language,
} from '../constants/language';
import { translate, type TranslationKey, type TranslationParams } from '../i18n';

interface LanguageContextValue {
  language: Language;
  setLanguage: (language: Language) => void;
  /** Flips to the other language — the toggle button has nothing else to decide. */
  toggleLanguage: () => void;
  t: (key: TranslationKey, params?: TranslationParams) => string;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

/**
 * Read on first render rather than in an effect, so the first paint is already in
 * the stored language instead of flashing English and correcting itself.
 * localStorage throws in private-mode Safari and when storage is disabled, hence
 * the guard — a broken preference should not take the whole app down.
 */
function readStoredLanguage(): Language {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return stored && isLanguage(stored) ? stored : DEFAULT_LANGUAGE;
  } catch {
    return DEFAULT_LANGUAGE;
  }
}

export function LanguageProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const [language, setLanguageState] = useState<Language>(readStoredLanguage);

  const setLanguage = useCallback((next: Language) => {
    setLanguageState(next);
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
    } catch {
      // Preference is lost on reload, but the switch still works for this session.
    }
    // Assistive tech and the browser's own text handling read this off the document,
    // which React does not own — so it is set here rather than in the markup.
    document.documentElement.lang = next;
  }, []);

  const toggleLanguage = useCallback(() => {
    setLanguage(language === 'en' ? 'bn' : 'en');
  }, [language, setLanguage]);

  const t = useCallback(
    (key: TranslationKey, params?: TranslationParams) => translate(language, key, params),
    [language]
  );

  const value = useMemo<LanguageContextValue>(
    () => ({ language, setLanguage, toggleLanguage, t }),
    [language, setLanguage, toggleLanguage, t]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used inside LanguageProvider');
  return ctx;
}

/** Sugar for the common case of needing only `t`. */
export function useTranslation(): LanguageContextValue {
  return useLanguage();
}
